import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useEmployeeAuth } from './EmployeePortalLayout';
import {
  DollarSign,
  TrendingUp,
  Award,
  CheckCircle,
  Clock,
  ChevronRight,
  AlertCircle,
  Star
} from 'lucide-react';

export const EmployeePay = () => {
  const { api } = useEmployeeAuth();
  const [payInfo, setPayInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayInfo();
  }, []);

  const fetchPayInfo = async () => {
    try {
      const response = await api.get('/api/employee-portal/my-pay');
      setPayInfo(response.data);
    } catch (error) {
      console.error('Error fetching pay info:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!payInfo) {
    return (
      <div className="p-4">
        <div className="bg-red-50 text-red-600 p-4 rounded-xl flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          <p>Unable to load pay information. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">My Pay</h1>
        <p className="text-gray-500 text-sm">Your earnings and tier progress</p>
      </div>

      {/* Current Tier Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-[#C6A87C] to-[#9A7B5C] text-white rounded-2xl p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-white/70 text-sm">Current Tier</p>
            <p className="text-3xl font-bold">{payInfo.tier_name}</p>
          </div>
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
            <DollarSign className="w-8 h-8" />
          </div>
        </div>
        <p className="text-white/80 text-sm mb-4">{payInfo.tier_description}</p>
        <div className="bg-white/20 rounded-xl p-4">
          <div className="flex justify-between items-center">
            <span className="text-white/70">Hourly Rate</span>
            <span className="text-2xl font-bold">${payInfo.hourly_rate.toFixed(2)}/hr</span>
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-white/70">Daily Cap</span>
            <span className="font-semibold">${payInfo.daily_cap}/day max</span>
          </div>
        </div>
      </motion.div>

      {/* Potential Earnings */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl p-4 shadow-sm"
      >
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-green-500" />
          <h2 className="font-semibold">Potential Earnings</h2>
        </div>
        
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-green-600">${payInfo.potential_earnings.daily}</p>
            <p className="text-xs text-gray-500">Daily</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-green-600">${payInfo.potential_earnings.weekly}</p>
            <p className="text-xs text-gray-500">Weekly</p>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <p className="text-2xl font-bold text-green-600">${payInfo.potential_earnings.monthly.toLocaleString()}</p>
            <p className="text-xs text-gray-500">Monthly</p>
          </div>
        </div>
        
        <div className="bg-yellow-50 rounded-xl p-3 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-yellow-700">
            {payInfo.potential_earnings.note}
          </p>
        </div>
      </motion.div>

      {/* Current Tier Requirements */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-white rounded-2xl p-4 shadow-sm"
      >
        <h3 className="font-semibold mb-3">Your Tier Requirements</h3>
        <div className="space-y-2">
          {payInfo.tier_requirements.map((req, i) => (
            <div key={i} className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">{req}</p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Next Tier */}
      {payInfo.next_tier && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-4 shadow-sm border-2 border-dashed border-[#C6A87C]/30"
        >
          <div className="flex items-center gap-2 mb-3">
            <Star className="w-5 h-5 text-[#C6A87C]" />
            <h3 className="font-semibold">Next Level: {payInfo.next_tier.name}</h3>
          </div>
          <p className="text-[#C6A87C] font-bold text-lg mb-2">{payInfo.next_tier.hourly_range}/hr</p>
          <p className="text-sm text-gray-600 mb-4">{payInfo.next_tier.description}</p>
          
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Requirements to advance:
          </p>
          <div className="space-y-2">
            {payInfo.next_tier.requirements.map((req, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full border-2 border-gray-300 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-gray-600">{req}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* All Tiers Overview */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
        className="bg-white rounded-2xl p-4 shadow-sm"
      >
        <h3 className="font-semibold mb-4">Pay Tier Structure</h3>
        <div className="space-y-3">
          {payInfo.all_tiers.map((tier) => (
            <div
              key={tier.tier}
              className={`p-4 rounded-xl border-2 transition-colors ${
                tier.is_current 
                  ? 'border-[#C6A87C] bg-[#C6A87C]/5' 
                  : 'border-gray-100'
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-semibold">{tier.name}</span>
                <span className={`font-bold ${tier.is_current ? 'text-[#C6A87C]' : 'text-gray-700'}`}>
                  {tier.hourly_range}/hr
                </span>
              </div>
              <p className="text-sm text-gray-500">{tier.description}</p>
              {tier.is_current && (
                <span className="inline-block mt-2 px-2 py-1 bg-[#C6A87C] text-white text-xs rounded-full">
                  Current
                </span>
              )}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default EmployeePay;
